/**
 * Security utilities for input validation and sanitization
 * Prevents XSS, injection attacks, and other security vulnerabilities
 */

/**
 * Sanitizes text input by removing potentially harmful characters and scripts
 * @param {string} input - The input string to sanitize
 * @returns {string} - Sanitized string
 */
export const sanitizeTextInput = (input) => {
  if (typeof input !== 'string') return '';
  
  return input
    // Remove HTML tags and scripts
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<[^>]*>/g, '')
    // Remove potentially dangerous characters
    .replace(/[<>'"&]/g, '')
    // Remove JavaScript event handlers
    .replace(/on\w+\s*=/gi, '')
    // Remove data URLs and javascript: protocols
    .replace(/data:/gi, '')
    .replace(/javascript:/gi, '')
    // Remove SQL injection patterns
    .replace(/(\b(union|select|insert|update|delete|drop|create|alter|exec|execute)\b)/gi, '');
    // Note: Removed .trim() to preserve user's intended spacing
};

/**
 * Validates and sanitizes textarea input with length limits
 * @param {string} input - The input string to validate
 * @param {number} maxLength - Maximum allowed length
 * @returns {object} - {isValid: boolean, sanitized: string, error: string}
 */
export const validateTextareaInput = (input, maxLength = 1300) => {
  if (typeof input !== 'string') {
    return { isValid: false, sanitized: '', error: 'Invalid input type' };
  }

  const sanitized = sanitizeTextInput(input);
  
  if (sanitized.length > maxLength) {
    return { 
      isValid: false, 
      sanitized: sanitized.substring(0, maxLength), 
      error: `Input exceeds maximum length of ${maxLength} characters` 
    };
  }

  return { isValid: true, sanitized, error: null };
};

/**
 * Validates date input to prevent malicious date values
 * @param {string} dateString - Date string to validate
 * @returns {object} - {isValid: boolean, sanitized: string, error: string}
 */
export const validateDateInput = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return { isValid: false, sanitized: '', error: 'Invalid date format' };
  }

  // Check for valid date format (YYYY-MM-DD) or year only (YYYY)
  const fullDateRegex = /^\d{4}-\d{2}-\d{2}$/;
  const yearOnlyRegex = /^\d{4}$/;
  
  if (!fullDateRegex.test(dateString) && !yearOnlyRegex.test(dateString)) {
    return { isValid: false, sanitized: '', error: 'Invalid date format' };
  }

  let date;
  if (yearOnlyRegex.test(dateString)) {
    // Handle year-only input
    const year = parseInt(dateString, 10);
    const now = new Date();
    const minYear = 1950;
    const maxYear = now.getFullYear() - 16; // 16 years ago
    
    if (year < minYear || year > maxYear) {
      return { isValid: false, sanitized: '', error: 'Year out of valid range' };
    }
    
    return { isValid: true, sanitized: dateString, error: null };
  } else {
    // Handle full date input
    date = new Date(dateString);
    const now = new Date();
    const minDate = new Date('1950-01-01');
    const maxDate = new Date(now.getFullYear() - 16, 11, 31); // 16 years ago

    if (isNaN(date.getTime())) {
      return { isValid: false, sanitized: '', error: 'Invalid date' };
    }

    if (date < minDate || date > maxDate) {
      return { isValid: false, sanitized: '', error: 'Date out of valid range' };
    }
  }

  return { isValid: true, sanitized: dateString, error: null };
};

/**
 * Validates numeric input (for sliders, etc.)
 * @param {any} value - Value to validate
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @returns {object} - {isValid: boolean, sanitized: number, error: string}
 */
export const validateNumericInput = (value, min = 0, max = 10) => {
  const numValue = parseInt(value, 10);
  
  if (isNaN(numValue)) {
    return { isValid: false, sanitized: min, error: 'Invalid numeric input' };
  }

  if (numValue < min || numValue > max) {
    const clampedValue = Math.max(min, Math.min(max, numValue));
    return { 
      isValid: false, 
      sanitized: clampedValue, 
      error: `Value must be between ${min} and ${max}` 
    };
  }

  return { isValid: true, sanitized: numValue, error: null };
};

/**
 * Validates array input (for tags, etc.)
 * @param {any} input - Input to validate
 * @param {number} maxLength - Maximum array length
 * @returns {object} - {isValid: boolean, sanitized: array, error: string}
 */
export const validateArrayInput = (input, maxLength = 10) => {
  if (!Array.isArray(input)) {
    return { isValid: false, sanitized: [], error: 'Invalid array input' };
  }

  if (input.length > maxLength) {
    return { 
      isValid: false, 
      sanitized: input.slice(0, maxLength), 
      error: `Maximum ${maxLength} items allowed` 
    };
  }

  // Sanitize each item in the array
  const sanitized = input.map(item => sanitizeTextInput(String(item))).filter(item => item.length > 0);
  
  return { isValid: true, sanitized, error: null };
};

/**
 * Escapes HTML entities to prevent XSS
 * @param {string} str - String to escape
 * @returns {string} - HTML-escaped string
 */
export const escapeHtml = (str) => {
  if (typeof str !== 'string') return '';
  
  const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;'
  };
  
  return str.replace(/[&<>"'/]/g, (match) => htmlEscapes[match]);
};

/**
 * Validates form data before submission
 * @param {object} formData - Form data to validate
 * @returns {object} - {isValid: boolean, sanitized: object, errors: array}
 */
export const validateFormData = (formData) => {
  const errors = [];
  const sanitized = {};

  // Validate city
  if (formData.city) {
    const cityValidation = validateTextareaInput(formData.city, 100);
    if (!cityValidation.isValid) {
      errors.push(`City: ${cityValidation.error}`);
    }
    sanitized.city = cityValidation.sanitized;
  }

  // Validate date of birth
  if (formData.date_of_birth) {
    const dateValidation = validateDateInput(formData.date_of_birth);
    if (!dateValidation.isValid) {
      errors.push(`Date of birth: ${dateValidation.error}`);
    }
    sanitized.date_of_birth = dateValidation.sanitized;
  }

  // Validate gender
  if (formData.gender) {
    const genderValidation = validateTextareaInput(formData.gender, 20);
    sanitized.gender = genderValidation.sanitized;
  }

  // Validate looking_for
  if (formData.looking_for) {
    const lookingForValidation = validateTextareaInput(formData.looking_for, 100);
    sanitized.looking_for = lookingForValidation.sanitized;
  }

  // Validate tags
  if (formData.tags) {
    const tagsValidation = validateArrayInput(formData.tags, 20);
    if (!tagsValidation.isValid) {
      errors.push(`Tags: ${tagsValidation.error}`);
    }
    sanitized.tags = tagsValidation.sanitized;
  }

  // Validate bars (slider values)
  if (formData.bars) {
    sanitized.bars = formData.bars.map(bar => {
      const valueValidation = validateNumericInput(bar.value, 0, 10);
      return {
        ...bar,
        value: valueValidation.sanitized
      };
    });
  }

  // Validate questions
  if (formData.questions) {
    sanitized.questions = formData.questions.map(question => {
      const answerValidation = validateTextareaInput(question.answer, 1300);
      return {
        ...question,
        answer: answerValidation.sanitized
      };
    });
  }

  // Validate selectedQuestions array
  if (formData.selectedQuestions) {
    const selectedQuestionsValidation = validateArrayInput(formData.selectedQuestions, 20);
    if (!selectedQuestionsValidation.isValid) {
      errors.push(`Selected questions: ${selectedQuestionsValidation.error}`);
    }
    sanitized.selectedQuestions = selectedQuestionsValidation.sanitized;
  }

  return {
    isValid: errors.length === 0,
    sanitized,
    errors
  };
};
